// RT-20: HTML and SVG output sanitization.
// Also provides client-side JSON parsing that handles common AI output issues.

/** Escape literal newlines/tabs inside JSON string values (AI models often emit these). */
function fixJsonStrings(s: string): string {
  let result = '';
  let inString = false;
  let escape = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (escape) { result += c; escape = false; continue; }
    if (c === '\\') { result += c; escape = true; continue; }
    if (c === '"') { inString = !inString; result += c; continue; }
    if (inString) {
      if (c === '\n') { result += '\\n'; continue; }
      if (c === '\r') { result += '\\r'; continue; }
      if (c === '\t') { result += '\\t'; continue; }
    }
    result += c;
  }
  return result;
}

/** Parse JSON returned by AI, strips code fences and handles literal newlines in strings. */
export function safeParseJSON<T = unknown>(raw: string): T | null {
  if (!raw) return null;
  const clean = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/, '').trim();
  try { return JSON.parse(clean) as T; } catch { /* continue */ }
  try { return JSON.parse(fixJsonStrings(clean)) as T; } catch { /* continue */ }
  const match = clean.match(/\{[\s\S]*\}/);
  if (match) {
    try { return JSON.parse(match[0]) as T; } catch { /* continue */ }
    try { return JSON.parse(fixJsonStrings(match[0])) as T; } catch { /* ignore */ }
  }
  return null;
}


// React already escapes JSX expressions, these helpers are for contexts
// where raw HTML is constructed (dangerouslySetInnerHTML, SVG text, templates).

/** HTML-escape a string for safe inclusion in HTML attributes or text nodes. */
export function sanitizeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Sanitize for SVG text elements (tspan, text).
 * Extends sanitizeHtml with additional SVG-specific injection patterns.
 */
export function sanitizeSvg(input: string): string {
  return sanitizeHtml(input)
    .replace(/url\s*\(/gi, '')       // prevent url() injection
    .replace(/javascript\s*:/gi, '') // prevent javascript: URIs
    .replace(/expression\s*\(/gi, ''); // prevent CSS expression()
}
